import React from "react";
import { useParams } from "react-router-dom";
import { CombinedWarehouseOrderItemDto, Delivery } from "@lib/ShiOneClient";
import { DateTime } from "luxon";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Typography from "@mui/material/Typography";
import Grid from "@mui/material/Grid";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import { useGetWarehouseOrders } from "@features/assets-feature/hooks/warehouse/useGetWarehouseOrders";
import { Skeleton } from "@mui/material";
import { pulse } from "../../../../v2/src/theme/pulse";

type DeliveryStatus = {
  deliveredDtm: string;
  delivered: boolean;
};

const DemoShipping = () => {
  // @ts-ignore
  const { data, isLoading } = useGetWarehouseOrders();
  let { id } = useParams();

  if (isLoading) {
    return (
      <Skeleton
        height={"65vh"}
        sx={{
          animation: `${pulse} 1.5s infinite`,
        }}
      />
    );
  }

  const orderItem = data?.items?.find(
    (orderItem: CombinedWarehouseOrderItemDto) =>
      orderItem.uniqueIdentifier === id
  );
  const delivered = "Delivered";
  const inProcess = "Expected Delivery";

  function deliveryDisplay(deliveryInfo: Delivery) {
    if (!deliveryInfo.shipDate) return;
    const shipDate =
      DateTime.fromJSDate(deliveryInfo?.shipDate) ?? DateTime.now();
    const hourDifference = DateTime.utc().diff(shipDate, "hours").hours;

    let deliveryStatus: DeliveryStatus = {
      delivered: false,
      deliveredDtm: "",
    };

    const iplTime = 1;
    const groundTime = 120;
    const airTime = 48;

    switch (deliveryInfo.deliveryMethod) {
      case "IPL - Lightspeed":
        deliveryStatus.deliveredDtm = shipDate
          ?.plus({ hours: iplTime })
          .toFormat("MM-dd-yyyy")
          .toString();
        deliveryStatus.delivered = hourDifference > iplTime;
        break;
      case "IPL - Ground":
        deliveryStatus.deliveredDtm = shipDate
          ?.plus({ hours: groundTime })
          .toFormat("MM-dd-yyyy")
          .toString();
        deliveryStatus.delivered = hourDifference > groundTime;
        break;
      case "IPL - Air":
        deliveryStatus.deliveredDtm = shipDate
          ?.plus({ hours: airTime })
          .toFormat("MM-dd-yyyy")
          .toString();
        deliveryStatus.delivered = hourDifference > airTime;
        break;
      default:
        console.log("Unknown delivery method:");
    }

    return deliveryStatus;
  }

  return (
    <div>
      <Grid
        container
        xs={12}
        alignItems="center"
        style={{
          backgroundImage: "linear-gradient(to bottom, black, blue)",
          backgroundRepeat: "no-repeat",
          color: "white",
          paddingBottom: "10px",
          paddingTop: "10px",
          marginBottom: "10px",
        }}
      >
        <Grid item xs={1}>
          <AutoAwesomeIcon style={{ fontSize: "5em" }} />
        </Grid>
        <Grid xs={6}>
          <Grid item>
            <h2 style={{ marginBottom: "-.75em" }}>
              Welcome to Interplanetary Logistics
            </h2>
          </Grid>
          <Grid item>
            <p>
              Your trusted partner for interplanetary shipping and logistics
              solutions.
            </p>
          </Grid>
        </Grid>
      </Grid>
      <div style={{ height: "90vh", overflowY: "auto", marginLeft: "10px" }}>
        <Grid container>
          {orderItem?.deliveryInfo?.map((item: Delivery, index: number) => (
            <Grid xs={4} key={item.trackNum}>
              <Card
                key={item.trackNum}
                style={{
                  marginBottom: "10px",
                  marginRight: "10px",
                  paddingLeft: "10px",
                }}
              >
                <CardContent>
                  <Typography variant="body1">
                    Item {index + 1} of {orderItem?.deliveryInfo?.length} in
                    shipment
                  </Typography>
                  <hr />
                  <Grid container spacing={2} style={{ paddingBottom: "10px" }}>
                    <Grid item xs={6}>
                      <Typography variant="h6">Shipment</Typography>
                      <Typography variant="h4">
                        <b>{item.trackNum}</b>
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="h6">
                        {deliveryDisplay(item)?.delivered
                          ? delivered
                          : inProcess}
                      </Typography>
                      <Typography variant="h4">
                        <b>{deliveryDisplay(item)?.deliveredDtm}</b>
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="h6">Shipping Method</Typography>
                      <Typography variant="h4">
                        <b>{item.deliveryMethod}</b>
                      </Typography>
                    </Grid>
                  </Grid>
                  <hr />
                  <Grid container spacing={2} style={{ paddingTop: "10px" }}>
                    <Grid item xs={6}>
                      <Typography variant="body1">
                        {deliveryDisplay(item)?.delivered
                          ? "Delivered To"
                          : "Delivering To"}
                      </Typography>
                      <Typography variant="body1">
                        {orderItem?.destination}
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="body1">Received By:</Typography>
                      <Typography variant="body1">
                        {deliveryDisplay(item)?.delivered
                          ? orderItem.deliveryName
                          : ""}
                      </Typography>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </div>
    </div>
  );
};

export default DemoShipping;
